import { Component } from 'react';
import { css } from 'react-emotion';
import * as React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withTranslation, Trans } from 'react-i18next';
import IdentityBadge from '../../common/IdentityBadge';
import DateRange from '../../common/DateRange';
import Button from '../../common/Button';
import ReadMorePopover from '../../common/ReadMorePopover';
import ReadableUserSub from '../../common/ReadableUserSub';
import compose from 'lodash/fp/compose';
import map from 'lodash/fp/map';
import take from 'lodash/fp/take';
import i18n from '../../../i18n/i18n';

import { Link } from 'react-router-dom';
import { getAllCountryCodes, getExcludedCountries } from '../../../utilities/countryHelpers';
import CountryModal from '../createContract/SetupShippingPricingTermStep/CountryModal';
import CountryDisplay from '../createContract/SetupShippingPricingTermStep/CountryDisplay';

import { Tooltip } from '@cimpress/react-components';
import StatusCell from '../../home/contractTable/StatusCell';
import EditGracePeriodButton from './EditGracePeriodButton';
import { TermType, IsShippingTermType } from '../../../constants/termTypes';
import AgreementTypeLabel from '../createContract/SetupShippingPricingTermStep/AgreementTypeLabel';
import EditModifierButton from './EditModifierButton';

const t = i18n.t.bind(i18n);

const containerStyle = css`
  display: flex;
  height: 100%;
  padding: 0 15px;
`;

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  margin-bottom: 25px;
`;

const termSectionTitleStyle = css`
  border-bottom: 1px solid lightgray;
  margin-bottom: 15px;
  padding-bottom: 5px;
`;

const termInfoListStyle = css`
  list-style: none;
  column-width: 250px;
  column-count: 3;
`;

const termInfoListItemStyle = css`
  margin-bottom: 18px;
  break-inside: avoid;
`;

const termInfoListItemTitleStyle = css`
  margin: 3px 0;
`;

const allCountriesLabel = {
  key: t('contractInfo:destinationCountriesTitle'),
  value: <span>{t('contractInfo:allCountries')}</span>
};

export class TermInfo extends Component {
  state = {
    showStatusModal: false
  };

  static propTypes = {
    termRevision: PropTypes.object.isRequired,
    latestRevisionId: PropTypes.number,
    isLayoutVertical: PropTypes.bool,
    priceModels: PropTypes.arrayOf(PropTypes.object),
    showDestinationModal: PropTypes.bool,
    editable: PropTypes.bool,
    setLatestRevision: PropTypes.func
  };

  onCloseModal = () => {
    this.setState({ showStatusModal: false });
  };

  onDestinationClick = () => {
    this.setState({ showStatusModal: true });
  };

  static defaultProps = {
    editable: false
  };

  renderVerticalView = (termInfoSections, destinationModal) => {
    const termInfoSectionsToRender = termInfoSections.map(section => (
      <div key={section.title} className="col-xs-6 col-md-12">
        <h5 className={termSectionTitleStyle}>{section.title}</h5>
        <ol className={termInfoListStyle}>
          {section.items.map(
            (info, idx) =>
              info && (
                <li className={termInfoListItemStyle} key={idx}>
                  <h6 className={termInfoListItemTitleStyle}>
                    {info.keyTooltip ? (
                      <Tooltip contents={info.keyTooltip}>
                        {info.key} <i className="fa fa-question-circle" />
                      </Tooltip>
                    ) : (
                      info.key
                    )}
                  </h6>
                  {info.value}
                </li>
              )
          )}
        </ol>
      </div>
    ));

    return (
      <React.Fragment>
        {termInfoSectionsToRender}
        {destinationModal}
      </React.Fragment>
    );
  };

  renderHorizontalView = (termInfoSections, destinationModal) => {
    const termInfoSectionsToRender = termInfoSections.map((section, idx) => (
      <ol className={termInfoListStyle} key={'section' + idx}>
        {section.items.map(
          (info, idx) =>
            info && (
              <li className={termInfoListItemStyle} key={idx}>
                <h6 className={termInfoListItemTitleStyle}>
                  {info.keyTooltip ? (
                    <Tooltip contents={info.keyTooltip}>
                      {info.key} <i className="fa fa-question-circle" />
                    </Tooltip>
                  ) : (
                    info.key
                  )}
                </h6>
                {info.value}
              </li>
            )
        )}
      </ol>
    ));

    return (
      <div className={containerStyle}>
        <div className={bodyStyle}>
          <h5 className={termSectionTitleStyle}>{t('contractInfo:infoSectionTitle')}</h5>
          <div>
            {termInfoSectionsToRender}
            {destinationModal}
          </div>
        </div>
      </div>
    );
  };

  getCountryList = destinationCountries => {
    const numberOfCountries = getAllCountryCodes().length;
    const showExcluded = destinationCountries.length > numberOfCountries / 2;

    return {
      countryListType: showExcluded
        ? t('contractInfo:excludedCountriesTitle')
        : t('contractInfo:destinationCountriesTitle'),
      countriesToDisplay: showExcluded ? getExcludedCountries(destinationCountries) : destinationCountries
    };
  };

  renderDestinationDisplay = destinationCountries => {
    if (destinationCountries.length === 0) {
      return allCountriesLabel;
    }

    const { countryListType, countriesToDisplay } = this.getCountryList(destinationCountries);

    return this.renderDestinationCountryList(countryListType, countriesToDisplay);
  };

  renderDestinationModal = destinationCountries => {
    const { countryListType, countriesToDisplay } = this.getCountryList(destinationCountries);
    const { showStatusModal } = this.state;

    return (
      <CountryModal
        onClose={this.onCloseModal}
        show={showStatusModal}
        countryCodes={countriesToDisplay}
        countryListType={countryListType}
      />
    );
  };

  renderDestinationCountryList = (countryListType, countriesToDisplay) => {
    const maxToDisplay = 10;

    const list = compose(
      take(maxToDisplay),
      map(countryCode => (
        <div key={countryCode}>
          <CountryDisplay countryCode={countryCode} />
        </div>
      ))
    )(countriesToDisplay);

    const displayLink = countriesToDisplay.length > maxToDisplay;

    if (displayLink) {
      list.push(
        <div key={'link'}>
          <Button bsStyle="link" onClick={this.onDestinationClick}>
            {t('contractInfo:viewAllCountries', { count: countriesToDisplay.length })}
          </Button>
        </div>
      );
    }

    const countryList = {
      key: countryListType,
      value: list
    };

    return countryList;
  };

  render() {
    const {
      t,
      termRevision,
      latestRevisionId,
      isLayoutVertical,
      priceModels,
      editable,
      setLatestRevision,
      v2Key
    } = this.props;
    const { additionalData = {} } = termRevision;

    const destinationCountriesDisplay = IsShippingTermType(termRevision.type)
      ? this.renderDestinationDisplay(termRevision.destinationCountries)
      : null;

    const hasModal = termRevision.destinationCountries && termRevision.destinationCountries.length > 10;
    const destinationModal =
      IsShippingTermType(termRevision.type) && hasModal
        ? this.renderDestinationModal(termRevision.destinationCountries)
        : null;
    const status = {
      key: t('common:commonTerminology.status'),
      value: (
        <div>
          <div>
            <StatusCell term={termRevision} latestRevisionId={latestRevisionId} />
          </div>
          <small>
            <Trans i18nKey="contractInfo:statusChangedOn">
              Status changed on <strong>{{ dateTime: termRevision.createdAt }}</strong>
            </Trans>
          </small>
          <br />
          <small>
            <span style={{ userSelect: 'none' }}>{t('contractInfo:updatedBy')} </span>
            <strong>{termRevision.createdByEmail || t('common:commonTerminology.mcpSystem')}</strong>
          </small>
        </div>
      )
    };

    const effectiveDates = {
      key: t('common:commonTerminology.effectiveDates'),
      value: <DateRange startDate={termRevision.effectiveStartDate} endDate={termRevision.effectiveEndDate} />
    };

    const seller = {
      key: t('common:commonTerminology.seller'),
      value: (
        <IdentityBadge
          identityType={termRevision.seller.type}
          identityId={termRevision.seller.id}
          identityName={termRevision.seller.name}
        />
      )
    };

    const buyer = {
      key: t('common:commonTerminology.buyer'),
      value: (
        <IdentityBadge
          identityType={termRevision.buyer.type}
          identityId={termRevision.buyer.id}
          identityName={termRevision.buyer.name}
        />
      )
    };

    const agreementType = {
      key: t('common:commonTerminology.contractType'),
      value: (
        <div>
          <AgreementTypeLabel term={termRevision} />
          {editable && <EditModifierButton termRevision={termRevision} setLatestRevision={setLatestRevision} />}
        </div>
      )
    };

    const gracePeriod = {
      key: t('common:commonTerminology.gracePeriod'),
      keyTooltip: t('contractInfo:gracePeriodDescription'),
      value: (
        <div>
          {!additionalData.gracePeriodConfiguration
            ? t('contractInfo:noGracePeriod')
            : t('contractInfo:gracePeriod', {
                duration: moment.duration(
                  additionalData.gracePeriodConfiguration.period,
                  additionalData.gracePeriodConfiguration.unit
                )
              })}
          {editable && <EditGracePeriodButton termRevision={termRevision} setLatestRevision={setLatestRevision} />}
        </div>
      )
    };
    const revision = {
      key: t('common:commonTerminology.revision'),
      value:
        termRevision.type !== TermType.SHIPPING_PRICE ? (
          <Link to={`/${termRevision.termId}/${v2Key}/changes?to=${termRevision.revisionId}`} state={editable}>
            {termRevision.revisionId} ({t('contractInfo:viewChanges')})
          </Link>
        ) : (
          termRevision.revisionId
        )
    };
    const latestRevision = { key: t('contractInfo:latestRevisionTitle'), value: latestRevisionId };
    const numItems = { key: t('contractInfo:noOfItemsTitle'), value: termRevision.itemsCount };
    const remarks = {
      key: t('contractInfo:remarksTitle'),
      value: (
        <span style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
          {termRevision.additionalData && termRevision.additionalData.comment}
        </span>
      )
    };

    // TODO: Should probably be more elegant here and check the price model read permission directly,
    // but for now, assume that if the user has permission to the current term's seller, they will have permission to the price model.
    const pms =
      priceModels && termRevision.userHasAuthorizationFor.seller
        ? {
            key: priceModels.length ? t('contractInfo:priceModelsOnContract', { count: priceModels.length }) : null,
            value: priceModels.map(pm => {
              const {
                id,
                revisionId,
                name = t('contractInfo:defaultNameForPriceModel'),
                author,
                possibleCurrencies = []
              } = pm;

              const pricingManagerUrl = `${
                process.env.REACT_APP_PRICINGMANAGER_URL
              }/models/${id}?revision=${revisionId}`;
              const uniqueKey = id + revisionId;
              const tooltipContents = (
                <div>
                  <KeyValue
                    tableKey="name"
                    tableValue={
                      <a target="_blank" rel="noopener noreferrer" href={pricingManagerUrl}>
                        <span>
                          {name} <i className="fa fa-external-link" />
                        </span>
                      </a>
                    }
                  />
                  <KeyValue tableKey="id" tableValue={id} />
                  <KeyValue tableKey="revisionId" tableValue={revisionId} />
                  <KeyValue
                    tableKey={t('contractInfo:author')}
                    tableValue={author && <ReadableUserSub userSub={author} />}
                  />
                  <KeyValue
                    tableKey={t('contractInfo:possibleCurrencies')}
                    tableValue={possibleCurrencies.join(', ')}
                  />
                </div>
              );
              return (
                <div key={uniqueKey} style={{ marginBottom: '5px' }}>
                  <ReadMorePopover contents={tooltipContents} readMoreContents={null} variety="popover">
                    <Button bsStyle="link">{name}</Button>
                  </ReadMorePopover>
                </div>
              );
            })
          }
        : null;

    // if this prop is not passed in, then the default will be horizontal
    if (isLayoutVertical) {
      const termInfoSections = [
        {
          title: t('contractInfo:infoSectionTitle'),
          items: [status, effectiveDates]
        },
        {
          title: t('contractInfo:partiesSectionTitle'),
          items: [seller, buyer, destinationCountriesDisplay]
        },
        {
          title: t('contractInfo:detailsSectionTitle'),
          items: [agreementType, gracePeriod, revision, latestRevision, numItems, pms, remarks]
        }
      ];

      return this.renderVerticalView(termInfoSections, destinationModal);
    }

    // horizontal layout
    const termInfoSections = [
      {
        items: [
          status,
          effectiveDates,
          agreementType,
          gracePeriod,
          seller,
          buyer,
          destinationCountriesDisplay,
          numItems,
          pms,
          revision,
          latestRevision,
          remarks
        ]
      }
    ];

    return this.renderHorizontalView(termInfoSections, destinationModal);
  }
}

export default withTranslation()(TermInfo);

class KeyValue extends Component {
  render() {
    const { tableKey, tableValue } = this.props;

    return tableKey && tableValue ? (
      <div style={{ wordBreak: 'break-word', marginBottom: '5px' }}>
        <b style={{ userSelect: 'none' }}>
          {tableKey}
          {': '}
        </b>
        {tableValue}
      </div>
    ) : null;
  }
}
